<template>
    <v-container fluid class="req-wrap" :dir="$vuetify.rtl ? 'rtl' : 'ltr'">
        <v-card outlined class="mx-auto req-card" max-width="900">
            <div class="sec-head">
                <v-icon small color="white" class="me-2">mdi-emoticon-happy-outline</v-icon>
                <span>{{ $t('Svc_StrategicSatisfaction') }}</span>
            </div>

            <!-- success -->
            <div v-if="isPosted" class="pa-8 text-center">
                <v-icon size="64" color="#1B8354">mdi-check-circle</v-icon>
                <h3 class="mt-3 mb-1">{{ $t('RequestSubmittedAndStarted') }}</h3>
                <p class="mb-4" style="color:#607d8b;">{{ $t('ReferenceNumberLabel') }} <b>{{ referenceNumber }}</b></p>
                <v-btn color="primary" depressed @click="goFollowup">{{ $t('TrackRequests') }}</v-btn>
            </div>

            <!-- form -->
            <v-form v-else ref="form" v-model="valid" class="pa-5" @submit.prevent="submit">
                <RequestHeaderFields v-model="header" />

                <label class="fld">{{ $t('RequestDescription') }} <span class="req">*</span></label>
                <v-textarea v-model="request.description" outlined dense rows="6" auto-grow
                            :rules="reqRule" counter="2000" maxlength="2000"
                            :placeholder="$t('RequestDetailsPlaceholder')" />

                <label class="fld mt-2">{{ $t('Attachments') }} <span class="opt">{{ $t('OptionalWordPdfExcel') }}</span></label>
                <v-file-input :value="files" outlined dense multiple show-size counter clearable
                              accept=".pdf,.doc,.docx,.xls,.xlsx"
                              prepend-icon=""
                              prepend-inner-icon="mdi-paperclip"
                              :rules="fileRule"
                              :placeholder="$t('AttachFiles')"
                              @change="addFiles">
                    <template v-slot:selection="{ index }">
                        <v-chip small close class="me-1 mb-1" @click:close="removeFile(index)">
                            {{ files[index] ? files[index].name : '' }}
                        </v-chip>
                    </template>
                </v-file-input>

                <div class="d-flex mt-4">
                    <v-btn type="submit" color="primary" depressed :loading="submitting" :disabled="submitting">
                        <v-icon small class="me-1">mdi-send</v-icon> {{ $t('SubmitRequest') }}
                    </v-btn>
                    <v-spacer />
                </div>
            </v-form>
        </v-card>
    </v-container>
</template>

<script>
    import { mapGetters } from 'vuex'
    import ProcessesService from '@/services/ProcessesService.js'
    import RequestHeaderFields, { emptyRequestHeader } from '@/components/app/process/RequestHeaderFields.vue'

    export default {
        components: { RequestHeaderFields },

        data() {
            return {
                valid: false,
                submitting: false,
                isPosted: false,
                referenceNumber: '',
                request: { description: '' },
                header: emptyRequestHeader(),
                files: [],
                reqRule: [v => (!!v && String(v).trim().length > 0) || this.$t('Required')],
                fileRule: [
                    v => !v || v.length === 0 || v.every(f => /\.(pdf|docx?|xlsx?)$/i.test(f.name)) || this.$t('FileMustBeWordPdfExcel'),
                ],
            }
        },
        computed: {
            ...mapGetters('user', ['structureId']),
        },
        methods: {
            goFollowup() {
                this.$router.push({ name: 'followup' })
            },
            // v-file-input replaces its value on every pick; merge instead so users can
            // add attachments across several picks. An empty emit = the clear icon.
            addFiles(selected) {
                if (!selected || selected.length === 0) {
                    this.files = []
                    return
                }
                const merged = [...this.files]
                for (const f of selected) {
                    if (!merged.some(x => x.name === f.name && x.size === f.size && x.lastModified === f.lastModified)) {
                        merged.push(f)
                    }
                }
                this.files = merged
            },
            removeFile(i) {
                this.files.splice(i, 1)
            },
            submit() {
                if (!this.$refs.form.validate()) return
                this.submitting = true
                const payload = { ...this.header, description: this.request.description, structureId: this.structureId }
                const formData = new FormData()
                formData.append('objectData', JSON.stringify(payload))
                ;(this.files || []).forEach(f => formData.append('files', f))

                ProcessesService.createStrategicSatisfactionRequest(formData).then(res => {
                    const result = res.data
                    if (result && result.success === false) {
                        this.$root.$notification(this.$t(result.message) || result.message, 'error')
                    } else {
                        this.referenceNumber = result ? result.referenceNumber : ''
                        this.isPosted = true
                    }
                }).catch(error => {
                    // Prefer the server's own keyword (e.g. NoPermissionToCreateProcess on a 403) so the
                    // user is told *why* it failed. A network failure rejects with no `response` at all,
                    // hence the guards; $te() keeps an untranslated keyword from rendering raw.
                    const key = error && error.response && error.response.data
                        ? error.response.data.message
                        : null
                    const text = key && this.$te(key) ? this.$t(key) : this.$t('ErrorOccurred')
                    this.$root.$notification(text, 'error')
                }).finally(() => { this.submitting = false })
            },
        },
    }
</script>

<style scoped>
    .req-wrap { padding: 18px 12px; }
    .req-card { border: 1px solid #e3e8ee !important; border-radius: 12px; overflow: hidden; box-shadow: 0 2px 12px rgba(27,131,84,.06) !important; }
    .sec-head {
        display: flex; align-items: center; font-weight: 700; font-size: 15px; color: #fff;
        padding: 14px 20px; background: linear-gradient(135deg, #1B8354 0%, #28a06a 100%);
    }
    .fld { display: block; font-weight: 600; font-size: 13px; color: #37474f; margin-bottom: 6px; }
    .req { color: #e53935; }
    .opt { color: #90a4ae; font-weight: 400; font-size: 12px; }
</style>
